import React, { PureComponent, Fragment } from 'react'
import i18n from 'i18next'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import ChartSelector from '../ChartSelector'
import colours from '../Chart/colours'
import Chart from '../Chart'
import ReportPageTitle from '../ReportPageTitle'
import OptionTitle from './OptionTitle'
import OptionResponse from '../OptionResponse'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionFooter from '../QuestionFooter'
import DebugInfo from '../DebugInfo'
import QuestionEdit from '../QuestionEdit'
import GroupedRows from './GroupedRows'

@observer
class Percent extends PureComponent {
    constructor(props) {
        super(props)
        const {
            store,
            reportStore,
            question,
            dataKey,
        } = props
        const { survey } = store
        this.categories = question.optionSet.options.map((option) => (
            getOptionTitle({
                langs: option.langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))
        this.data = reportStore.reportData.questions[dataKey]
        this.isMaxbiReport = survey.isMaxbi()
        this.showChart = this.isMaxbiReport ? reportStore.showChart(question) : true
        this.isPercentage = R.pathOr('%', ['settings', 'frontend', 'format'], question) === '%'
        this.state = {
            zeroOptions: this.getZeroOptions(),
            updated: new Date().getTime(),
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(question.optionSet.options.length).keys()),
                sortedIndexArray: Array.from(Array(question.optionSet.options.length).keys()),
            },
            chartType: R.pathOr('column', ['settings', 'report', 'graphType'], question),
        }
        this.chartColours = [
            colours.getSerieColour('survey'),
            colours.getSerieColour('comparison'),
        ]

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.setState({
                    zeroOptions: this.getZeroOptions(),
                    updated: props.reportStore.reportDataFetched,
                })
            },
        )
        this.chartType = question.settings.report.graphType
        reportStore.questionSorting[dataKey] = this.getQuestionSorting
    }

    componentWillUnmount() {
        const { reportStore, dataKey } = this.props
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    getZeroOptions = () => {
        const {
            reportStore,
            question,
            dataKey,
            reportFilter,
        } = this.props

        if (!question.settings.report.hideZero && !this.isMaxbiReport) return new Set()

        const data = R.or(reportStore.reportData.questions[dataKey], {})
        const questionPercentage = R.pathOr(0, ['pctOfResponses'], data)
        const comparisonPercentage = reportFilter.years
            .filter(({ id }) => (R.hasPath(['comparison', id.toLowerCase()], data)))
            .map(({ id }) => (
                R.pathOr(0, ['comparison', id.toLowerCase()], data)
            ))

        const zeroOptions = question.optionSet.options.reduce((set, { id }, index) => {
            const optionHasData = (
                (R.pathOr(0, [index], questionPercentage) > 0)
                || comparisonPercentage.some((comparison) => R.pathOr(0, [index], comparison) > 0)
            )

            if (!optionHasData) {
                set.add(id)
            }

            return set
        }, new Set())

        console.log('getZeroOptions', zeroOptions)

        return zeroOptions
    }

    getQuestionSorting = () => {
        const { sorting } = this.state
        return [sorting.sortedIndexArray, this.chartType]
    }

    colMapper = ({ path, type, id }, data, option, index) => {
        const {
            question, reportStore, store, reportFilter,
        } = this.props
        const { survey } = store
        let col = null
        if (type === 'pct') {
            col = (
                <td>
                    {`${R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)} ${this.isPercentage ? '%' : ''}`}
                </td>
            )
        } else if (type === 'OptionResponse') {
            col = (
                <td>
                    <OptionResponse
                        id={id}
                        value={R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                        reportStore={reportStore}
                        reportFilter={reportFilter}
                    />
                </td>
            )
        } else if (type === 'optionTitle') {
            col = (
                <th>
                    <OptionTitle
                        id={option.id}
                        reportStore={reportStore}
                        option={option}
                        locale={reportStore.locale}
                        defaultLocale={survey.metadata.defaultLocale}
                        initialLocale={question.metadata.initialLocale}
                        toggleOther={this.toggleOther}
                    />
                </th>
            )
        } else if (type === 'val') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data)}
                </td>
            )
        } else if (type === 'avg') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                </td>
            )
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const {
            reportStore, question, store, dataKey,
        } = this.props
        const { survey } = store
        const data = reportStore.reportData.questions[dataKey]
        console.log(`sortBy ${JSON.stringify(col)}`)

        const oldSortBy = sorting.sortBy

        let values = []
        if (!col.path) {
            sorting.sortBy = 'label'
            values = question.optionSet.options.reduce((newOptions, o) => {
                newOptions.push(getOptionTitle({
                    langs: o.langs,
                    locale: reportStore.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                }))
                return newOptions
            }, [])
        } else {
            sorting.sortBy = col.path
            values = R.pathOr(0, col.path.split('.'))(data)
        }
        const sortedIndexArray = Array
            .from(Array(values.length).keys())
            .sort((a, b) => (values[a] < values[b] ? -1 : values[a] > values[b] ? 1 : 0))

        if (sorting.sortBy !== oldSortBy || !sorting.direction) {
            sorting.direction = 'ASC'
        } else {
            sorting.direction = sorting.direction === 'ASC'
                ? 'DESC'
                : undefined
        }

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = sortedIndexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = sortedIndexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }

        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                !path && sortBy === 'label'
                || path === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    renderRows = (cols, filterRows) => {
        const { sorting, zeroOptions } = this.state
        const {
            question,
            reportStore,
            dataKey,
        } = this.props
        const data = R.or(reportStore.reportData.questions[dataKey], {})

        return sorting.sortedIndexArray.map((index) => {
            const option = question.optionSet.options[index]
            return (
                (!filterRows || filterRows.has(option.id))
                && !zeroOptions.has(option.id)
            )
                ? (
                    <tr className="col" key={`option_${option.id}`}>
                        {(
                            cols
                                .map((col) => (
                                    this.colMapper(col, data, option, index)
                                ))
                        )}
                        <td className="tlast" />
                    </tr>
                ) : null
        })
    }

    renderGroupedRows = (cols) => {
        const { store, question, reportStore } = this.props
        return (
            <GroupedRows
                question={question}
                renderRows={this.renderRows}
                cols={cols}
                reportStore={reportStore}
                store={store}
            />
        )
    }

    generateDataTable = () => {
        const {
            question,
            reportStore,
            dataKey,
        } = this.props

        const data = R.or(reportStore.reportData.questions[dataKey], {})
        const comparisonCols = Array.from(reportStore.yoyComparisons)
            .filter(([id]) => (R.hasPath(['comparison', id.toLowerCase()], data)))
            .map(([id, { label }]) => ({
                label: reportStore.comparisonTitle(label),
                path: `comparison.${id.toLowerCase()}`,
                type: 'pct',
            }))

        const cols = this.isMaxbiReport ? [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: i18n.t('report_question_response'),
                path: 'pctOfResponses',
                type: 'pct',
            },
        ] : [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: reportStore.surveyTitle(),
                path: 'pctOfResponses',
                type: 'pct',
            },
            ...comparisonCols,
        ]

        this.cols = cols.slice(1)
        return (
            <div style={{ overflowX: 'auto' }}>
                <table
                    className="datatable question tablesorter tablesorter-default"
                    style={{
                        width: '100%',
                    }}
                >
                    <thead>
                        <tr className="col tablesorter-headerRow">
                            {cols
                                .map((col) => (
                                    <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                        <div className="tablesorter-header-inner">
                                            {col.label}
                                            {this.sortIcon(col)}
                                        </div>
                                    </th>
                                ))}
                            <td className="tlast" />
                        </tr>
                    </thead>
                    <tbody>
                        {(question.hasHeaderGroups
                            ? this.renderGroupedRows(cols)
                            : this.renderRows(cols)
                        )}
                    </tbody>
                </table>
            </div>
        )
    }

    getChartCategorySet = () => {
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        const { question } = this.props
        console.log('sorted categoryMap', sortedIndexArray.map((sortedIndex) => question.optionSet.options[sortedIndex]))
        return sortedIndexArray.map((sortedIndex) => question.optionSet.options[sortedIndex])
    }

    getChartCategories = () => {
        const { question, reportStore, store } = this.props
        const { survey } = store
        const { sorting, zeroOptions } = this.state
        const { sortedIndexArray } = sorting
        return sortedIndexArray
            .filter((sortedIndex) => (
                !zeroOptions.has(question.optionSet.options[sortedIndex].id)
            )).map((sortedIndex) => (
                getOptionTitle({
                    langs: question.optionSet.options[sortedIndex].langs,
                    locale: reportStore.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                })
            ))
    }

    render() {
        const {
            question,
            store,
            reportStore,
            reportFilter,
            dataKey,
            pageId,
        } = this.props
        const { survey } = store
        const { chartType } = this.state
        const data = R.or(reportStore.reportData.questions[dataKey], {})
        return (
            <Panel cssClass="question">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={getQuestionTitle({
                                content: question.content.shortTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            h3={getQuestionTitle({
                                content: question.content.longTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                <div id={`questionData_${question.pageQuestionId}`}>
                    {this.generateDataTable()}
                </div>
                {this.showChart && data.hasResponses && (
                    <Fragment>
                        <ChartSelector
                            chartType={chartType}
                            onChange={(chartType) => { this.setState({ chartType }) }}
                        />
                        <Chart
                            colors={this.chartColours}
                            dataKey={dataKey}
                            survey={survey}
                            data={data}
                            pointEvent={this.chartFilter}
                            question={question}
                            reportFilter={reportFilter}
                            series={this.cols}
                            sortedIndexArray={this.state.sorting.sortedIndexArray}
                            pageQuestionId={question.pageQuestionId}
                            id={question.pageQuestionId}
                            categorySet={this.getChartCategorySet()}
                            chartType={chartType}
                            questionTypeCode={question.questionTypeCode}
                            kpiType={question.metadata.kpiType}
                            categories={this.getChartCategories()}
                            reportStore={reportStore}
                            percentages={this.isPercentage}
                        />
                        <QuestionFooter
                            dataKey={dataKey}
                            reportStore={reportStore}
                        />
                    </Fragment>
                )}
            </Panel>
        )
    }
}

export default Percent
